import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { storiesWithSlugs } from "@/lib/results-stories";
import { storyHeadline, storyCardAlt } from "@/lib/story-titles";
import { esStoryHeadline } from "@/lib/story-titles-es";
import { PageSection } from "@/components/site/PageSection";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import storyAccents from "@/data/story-accents.json";

const accents = storyAccents as Record<string, string>;
const SITE = "https://virginiabariatric.com";
const TITLE = "Before & After Share Cards | Virginia Bariatric";
const DESCRIPTION =
  "Browse every before-and-after share card from Dr. Matthew Fitzer's bariatric surgery patients, with paging and a full-screen preview.";

export const Route = createFileRoute("/results/share-cards")({
  component: ShareCardsGallery,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "noindex, follow" },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE}/results/share-cards` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ] : []),
    ],
    links: [{ rel: "canonical", href: `${SITE}/results/share-cards` }],
  }),
});

const PER_PAGE = 8;

function ShareCardsGallery() {
  const cards = useMemo(() => storiesWithSlugs(), []);
  const [page, setPage] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [lang, setLang] = useState<"en" | "es">("en");

  /** Cards are generated per language: English in /og/stories, Spanish in /es. */
  const cardSrc = (slug: string) =>
    lang === "es" ? `/og/stories/es/${slug}.jpg` : `/og/stories/${slug}.jpg`;

  const pageCount = Math.ceil(cards.length / PER_PAGE);
  const visible = cards.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  const step = useCallback(
    (delta: number) =>
      setOpenIndex((current) =>
        current === null ? current : (current + delta + cards.length) % cards.length
      ),
    [cards.length]
  );

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenIndex(null);
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIndex, step]);

  const active = openIndex === null ? null : cards[openIndex];

  return (
    <main>
      <PageSection className="py-12">
        <header className="text-center">
          <h1 className="text-3xl font-bold text-brand-logo md:text-4xl">
            Before &amp; After Share Cards
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            All {cards.length} social share cards generated for our patient stories. Select a card
            to view it full screen, or open the story page behind it.
          </p>
          <div
            className="mt-5 inline-flex rounded-full border border-border p-1"
            role="group"
            aria-label="Share card language"
          >
            {(["en", "es"] as const).map((code) => (
              <Button
                key={code}
                size="sm"
                variant={lang === code ? "default" : "ghost"}
                className="rounded-full"
                aria-pressed={lang === code}
                onClick={() => setLang(code)}
              >
                {code === "en" ? "English" : "Español"}
              </Button>
            ))}
          </div>
        </header>

        <ul className="mt-10 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map(({ slug, story }, i) => {
            const index = page * PER_PAGE + i;
            return (
              <li
                key={slug}
                className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(index)}
                  className="group relative block w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`View the ${story.label} share card full screen`}
                >
                  <img
                    src={cardSrc(slug)}
                    alt={storyCardAlt(story.label)}
                    width={1200}
                    height={630}
                    loading="lazy"
                    decoding="async"
                    className="w-full transition-opacity group-hover:opacity-90"
                  />
                  <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-background/85 p-2 text-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                    <Maximize2 className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-2 bg-background/90 px-3 py-2 text-left text-xs font-medium text-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                    <span
                      className="h-4 w-4 shrink-0 rounded-full border border-border"
                      style={{ backgroundColor: accents[slug] ?? "transparent" }}
                      aria-hidden="true"
                    />
                    <span className="truncate">{story.label}</span>
                    <span className="ml-auto font-mono uppercase text-muted-foreground">
                      {accents[slug] ?? "—"}
                    </span>
                  </span>

                </button>
                <div className="space-y-2 p-4 text-center">
                  <h2 className="text-base font-semibold text-foreground">
                    {lang === "es"
                      ? esStoryHeadline(slug, story.label)
                      : storyHeadline(story.label)}
                  </h2>
                  <Link
                    to="/results/story/$slug"
                    params={{ slug }}
                    className="inline-block text-sm font-medium text-brand-logo underline underline-offset-4"
                  >
                    View story page
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>

        <nav
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
          aria-label="Share card pages"
        >
          <Button
            variant="secondary"
            onClick={() => setPage((p) => Math.max(p - 1, 0))}
            disabled={page === 0}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </Button>
          <div className="flex flex-wrap justify-center gap-2">
            {Array.from({ length: pageCount }, (_, i) => (
              <Button
                key={i}
                variant={i === page ? "default" : "outline"}
                size="sm"
                onClick={() => setPage(i)}
                aria-current={i === page ? "page" : undefined}
                aria-label={`Page ${i + 1} of ${pageCount}`}
              >
                {i + 1}
              </Button>
            ))}
          </div>
          <Button
            variant="secondary"
            onClick={() => setPage((p) => Math.min(p + 1, pageCount - 1))}
            disabled={page === pageCount - 1}
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </nav>
      </PageSection>

      {active && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${active.story.label} share card`}
          className="fixed inset-0 z-50 flex flex-col bg-foreground/95 p-4 md:p-8"
          onClick={() => setOpenIndex(null)}
        >
          <div
            className="flex flex-1 flex-col items-center justify-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={cardSrc(active.slug)}
              alt={storyCardAlt(active.story.label)}
              className="max-h-[75vh] w-auto max-w-full rounded-lg shadow-2xl"
            />
            <p className="max-w-2xl text-center text-sm text-background">
              {active.story.caption}
            </p>
            <div className="flex items-center gap-3">
              <Button variant="secondary" onClick={() => step(-1)} aria-label="Previous card">
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Prev
              </Button>
              <span className="text-sm text-background">
                {(openIndex ?? 0) + 1} / {cards.length}
              </span>
              <Button variant="secondary" onClick={() => step(1)} aria-label="Next card">
                Next
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
          <Button
            variant="secondary"
            size="icon"
            className="absolute right-4 top-4"
            onClick={() => setOpenIndex(null)}
            aria-label="Close full-screen preview"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}
    </main>
  );
}
